/**
 * Branding DTOs for HA-106 Client Branded Content View
 * Matches backend Healio.Dtos.Branding types
 */

/**
 * Complete branding configuration for a practice.
 * Returned from authenticated endpoint: GET /api/v1/practice/branding
 */
export interface PracticeBrandingDto {
  practiceId: string;

  // Logo
  logoUrl: string | null;
  logoFilename: string | null;
  logoContentType: string | null;
  logoSizeBytes: number;
  logoUploadedAtUtc: string | null;

  // Colors
  primaryColor: string;
  secondaryColor: string;
  accentColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;

  // Subdomain/Domain
  subdomain: string | null;
  subdomainVerified: boolean;
  customDomain: string | null;
  customDomainVerified: boolean;

  // Display
  displayName: string | null;
  tagline: string | null;
  fontFamily: string | null;
  showPoweredBy: boolean;

  // Email Branding
  emailHeaderColor: string | null;
  emailFooterText: string | null;

  // Metadata
  version: number;
  updatedAtUtc: string;
  updatedBy: string | null;
}

/**
 * Branding preview data for client portal and public access.
 * Returned from both authenticated and public endpoints.
 */
export interface BrandingPreviewDto {
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;
  displayName: string | null;
  tagline: string | null;
  fontFamily: string | null;
  showPoweredBy: boolean;
}

/**
 * Default branding values (HealioSpace brand colors)
 */
export const DEFAULT_BRANDING: BrandingPreviewDto = {
  logoUrl: null,
  primaryColor: '#4A90D9',
  secondaryColor: '#6B7280',
  accentColor: null,
  backgroundColor: null,
  textColor: null,
  displayName: 'HealioSpace',
  tagline: null,
  fontFamily: null,
  showPoweredBy: true,
};

/**
 * Extract preview data from full branding DTO
 */
export function toBrandingPreview(branding: PracticeBrandingDto): BrandingPreviewDto {
  return {
    logoUrl: branding.logoUrl,
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
    accentColor: branding.accentColor,
    backgroundColor: branding.backgroundColor,
    textColor: branding.textColor,
    displayName: branding.displayName,
    tagline: branding.tagline,
    fontFamily: branding.fontFamily,
    showPoweredBy: branding.showPoweredBy,
  };
}

/**
 * Get CSS variables object from branding
 */
export function getBrandingCssVars(branding: BrandingPreviewDto): Record<string, string> {
  const vars: Record<string, string> = {
    '--branding-primary-color': branding.primaryColor || DEFAULT_BRANDING.primaryColor,
    '--branding-secondary-color': branding.secondaryColor || DEFAULT_BRANDING.secondaryColor,
  };

  if (branding.accentColor) {
    vars['--branding-accent-color'] = branding.accentColor;
  }
  if (branding.backgroundColor) {
    vars['--branding-background-color'] = branding.backgroundColor;
  }
  if (branding.textColor) {
    vars['--branding-text-color'] = branding.textColor;
  }
  if (branding.fontFamily) {
    vars['--branding-font-family'] = branding.fontFamily;
  }

  return vars;
}
